// Import necessary functions and classes from Redux Toolkit and Howler.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Howl } from "howler";
import { closeModal } from "./modalSlice"; // Import action to close modal
import { GAME_SOUNDS, SOUND_NAME } from "../../sounds"; // Import sound constants

// Cache for Howl instances to avoid reloading sounds
const HowlCache = {};

// Object to store the duration of each sound
const soundDurations = {};

// Asynchronous thunk action to load sounds
export const loadSounds = createAsyncThunk(
  "sounds/loadSounds",
  async (_, { rejectWithValue }) => {
    // Iterate over each sound in the GAME_SOUNDS array
    for (const sound of GAME_SOUNDS.sounds) {
      // Check if the sound is not already cached
      if (!HowlCache[sound.name]) {
        // Create a new Howl instance for the sound
        HowlCache[sound.name] = new Howl({
          src: [sound.url], // URL of the sound file
          loop: sound.loop || false, // Whether the sound should loop
          volume: sound.volume || 1.0, // Volume of the sound
        });

        try {
          // Wait for the sound to load
          await new Promise((resolve, reject) => {
            const howl = HowlCache[sound.name];
            howl.once("load", () => {
              soundDurations[sound.name] = howl.duration(); // Store the duration of the sound
              resolve();
            });
            howl.once("loaderror", (id, error) => reject(error)); // Handle load error
          });
        } catch (error) {
          console.error(`Failed to load sound "${sound.name}":`, error);
          return rejectWithValue(`Failed to load sound: ${sound.name}`); // Return error if loading fails
        }
      }
    }

    // Return the mapping of sound names to durations
    return {
      soundKeys: Object.keys(HowlCache), // List of sound names
      durations: soundDurations, // Object containing sound durations
    };
  }
);

// Create a slice for sound-related state and actions
const soundsSlice = createSlice({
  name: "sounds", // Name of the slice
  initialState: {
    soundEffectsEnabled: true, // Whether sound effects are enabled
    backgroundMusicEnabled: false, // Whether background music is enabled
    isMuted: false, // Global mute state
    soundKeys: [], // List of loaded sound keys
    status: "idle", // Loading status
    error: null, // Error message if loading fails
  },
  reducers: {
    // Toggle sound effects on or off
    toggleSoundEffects(state) {
      state.soundEffectsEnabled = !state.soundEffectsEnabled;
      Object.entries(HowlCache).forEach(([key, sound]) => {
        if (key !== SOUND_NAME.backgroundMusic)
          sound.mute(!state.soundEffectsEnabled);
      });
    },

    // Toggle background music on or off
    toggleBackgroundMusic(state) {
      state.backgroundMusicEnabled = !state.backgroundMusicEnabled;
      const backgroundMusic = HowlCache[SOUND_NAME.backgroundMusic];
      if (backgroundMusic) {
        if (state.backgroundMusicEnabled) {
          backgroundMusic.play();
        } else {
          backgroundMusic.stop();
        }
      }
    },

    // Enable background music
    enableBackgroundMusic(state) {
      state.backgroundMusicEnabled = true;
      const backgroundMusic = HowlCache[SOUND_NAME.backgroundMusic];
      if (backgroundMusic && !backgroundMusic.playing()) {
        backgroundMusic.play();
      }
    },

    // Disable background music
    disableBackgroundMusic(state) {
      state.backgroundMusicEnabled = false;
      const backgroundMusic = HowlCache[SOUND_NAME.backgroundMusic];
      if (backgroundMusic && backgroundMusic.playing()) {
        backgroundMusic.stop();
      }
    },

    // Toggle global mute state
    toggleGlobalMute(state) {
      state.isMuted = !state.isMuted;
      Object.values(HowlCache).forEach((sound) => sound.mute(state.isMuted));
    },

    // Play a specific sound
    playSound(state, action) {
      const sound = HowlCache[action.payload];
      if (sound) sound.play();
    },

    // Stop a specific sound
    stopSound(state, action) {
      const sound = HowlCache[action.payload];
      if (sound && sound.playing()) sound.stop();
    },

    // Stop all sounds
    stopAllSounds() {
      Object.values(HowlCache).forEach((sound) => sound.stop());
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSounds.pending, (state) => {
        state.status = "loading"; // Set status to loading when sounds are being loaded
        state.error = null; // Clear any previous errors
      })
      .addCase(loadSounds.fulfilled, (state, action) => {
        state.status = "succeeded"; // Set status to succeeded when sounds are loaded
        state.soundKeys = action.payload.soundKeys; // Update sound keys
        state.soundDurations = {
          ...state.soundDurations,
          ...action.payload.durations, // Update sound durations
        };
      })
      .addCase(loadSounds.rejected, (state, action) => {
        state.status = "failed"; // Set status to failed if loading fails
        state.error = action.error.message; // Store error message
      });
  },
});

// Thunk action to enable background music and close modal
export const enableBackgroundMusicAndCloseModal = () => (dispatch) => {
  dispatch(soundsSlice.actions.enableBackgroundMusic());
  dispatch(closeModal());
};

// Thunk action to disable background music and close modal
export const disableBackgroundMusicAndCloseModal = () => (dispatch) => {
  dispatch(soundsSlice.actions.disableBackgroundMusic());
  dispatch(closeModal());
};

// Export the reducer actions for use in components
export const {
  toggleSoundEffects,
  toggleBackgroundMusic,
  toggleGlobalMute,
  playSound,
  stopSound,
  stopAllSounds,
} = soundsSlice.actions;

// Export the reducer to be used in the store
export default soundsSlice.reducer;
